import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Grid, GridColumn, GridRow, Icon } from "semantic-ui-react";

import { TYRE_LOCATIONS, TYRE_POSITIONS } from "models";
import { TyreTableRow } from "modules/AppointmentDetails/components/Checks/components/Check/components/QuestionResults/components/TyreQuestionResults/components/TyreTable/components";
import { DBBLabelsModal } from "modules/CarDetails/components/Tyres/components";
import { convertQuestionResultsToDBBLabels } from "modules/CarDetails/components/Tyres/components/DBBLabelsModal/utils";

import type { TFunction } from "i18next";
import type { Appointment, ChecklistTemplate } from "models";
import type { SortedQuestionResult } from "modules/AppointmentDetails/hooks";
import type { SemanticWIDTHS } from "semantic-ui-react";

type TyreTableProps = {
  appointment: Appointment;
  questionResults: SortedQuestionResult[];
  icon: string;
  title: string;
  tyreLocation: TYRE_LOCATIONS;
  template?: ChecklistTemplate | null;
  readOnly?: boolean;
};

const getAxleName = (questionResult: SortedQuestionResult, truckSpareIndex: number, t: TFunction) => {
  if (questionResult.tyre_position === TYRE_POSITIONS.TruckSpare) return `${t("v8_spare_tyre", "Spare Tyre")} ${questionResult.truck_axle - truckSpareIndex + 1}`;

  return `${t("v8_axle", "Axle")} ${questionResult.truck_axle}`;
};

export const TyreTable = ({ appointment, questionResults, icon, title, tyreLocation, template, readOnly }: TyreTableProps) => {
  const { t } = useTranslation();

  const [showDBBModal, setShowDBBModal] = useState(false);

  const checkListColumns: { header: string; width: SemanticWIDTHS }[] = [
    { header: t("v8_question", "Question"), width: 2 },
    { header: t("v8_size", "Size"), width: 2 },
    { header: t("v8_type", "Type"), width: 4 },
    { header: t("v8_brand", "Brand"), width: 1 },
    { header: t("v8_profile", "Profile"), width: 1 },
    { header: t("v8_remarks", "Remarks"), width: 3 },
    { header: "", width: 3 }
  ];

  const questionsByAxle = Map.groupBy(questionResults, q => q.truck_axle);
  let truckSpareIndex = -1;
  for (const [axle, questions] of questionsByAxle) {
    if (questions.some(q => q.tyre_position === TYRE_POSITIONS.TruckSpare)) {
      truckSpareIndex = axle;
      break;
    }
  }

  return (
    <div className="TyreTable">
      <div className="tyre-title-container">
        <div className="tyre-title">
          {<Icon className={icon} />}
          <p>{title}</p>
        </div>
        {!readOnly && (
          <div className="tyre-title__wrapper">
            {tyreLocation === TYRE_LOCATIONS.InStorage && appointment.car?.in_storage_tyres?.dbb_location_name && (
              <>
                <span className="tyre-title__wrapper__storage">{`${t("v8_storage_location", "Storage location")}: `}</span>
                <span className="tyre-title__wrapper__locationName">{appointment.car.in_storage_tyres.dbb_location_name}</span>
              </>
            )}
            <Button basic className="light" size="tiny" onClick={() => setShowDBBModal(true)}>
              <p>{t("v8_tire_labels", "Tire Labels")}</p>
            </Button>
          </div>
        )}
      </div>
      <Grid columns={16}>
        <GridRow className="header">
          {checkListColumns.map(item => (
            <GridColumn key={item.header} width={item.width}>
              <p>{item.header}</p>
            </GridColumn>
          ))}
        </GridRow>
        {Array.from(questionsByAxle, ([axle, questions]) => (
          <React.Fragment key={"axle-" + axle}>
            {axle > 0 && questions.length > 0 && (
              <GridRow className="axle-header">
                <GridColumn width={16}>
                  <p>{getAxleName(questions[0], truckSpareIndex, t)}</p>
                </GridColumn>
              </GridRow>
            )}
            {questions.map(question => (
              <React.Fragment key={question.id}>
                <TyreTableRow questionResult={question} template={template} appointment={appointment} tyreLocation={tyreLocation} readOnly={readOnly} />
              </React.Fragment>
            ))}
          </React.Fragment>
        ))}
      </Grid>
      {!readOnly && (
        <>
          <DBBLabelsModal
            open={showDBBModal}
            onClose={() => setShowDBBModal(false)}
            tyreLabels={convertQuestionResultsToDBBLabels(questionResults)}
            appointment={appointment}
            car={appointment.car}
          />
        </>
      )}
    </div>
  );
};
